import { AlertTriangle, LoaderCircle, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { GameScreen } from './components/GameScreen.jsx';
import { Lobby } from './components/Lobby.jsx';
import { WaitingRoom } from './components/WaitingRoom.jsx';
import { useGameSocket } from './hooks/useGameSocket.js';
import { MainMenu, OfflineSetup } from './components/MainMenu.jsx';
import { OfflineGameController } from './offline/OfflineGameController.js';
import { OfflineSaveStore } from './offline/saveStore.js';

// View state only: survives reload in an open tab, not a new app session.
const ONLINE_VIEW_KEY = 'kapitalia-online-view-v1';
const resumeOpenOnlineView = () => {
  const roomCode = sessionStorage.getItem(ONLINE_VIEW_KEY);
  const joinCode = window.location.pathname.match(/^\/join\/([A-Z0-9]{4})\/?$/i)?.[1]?.toUpperCase();
  return Boolean(roomCode && (!joinCode || joinCode === roomCode));
};

function LoadingScreen() {
  return (
    <main className="loading-screen">
      <div className="brand-lockup brand-lockup--large"><span className="brand-mark" aria-hidden="true"><i /><i /><i /></span><span>Капиталия</span></div>
      <LoaderCircle className="spin-slow" size={28} />
      <p>Восстанавливаем игровую сессию…</p>
    </main>
  );
}

function OnlinePlay({ onHome, onOffline, onRetry, resumeSavedSession }) {
  const gameSocket = useGameSocket({ resumeSavedSession });
  const { state, session, connected, booting, error, clearError, connectionFailed } = gameSocket;
  useEffect(() => {
    if (state && session?.roomCode === state.code) sessionStorage.setItem(ONLINE_VIEW_KEY, state.code);
    else if (!booting) sessionStorage.removeItem(ONLINE_VIEW_KEY);
  }, [state, session, booting]);

  let content;
  if (connectionFailed && !state) content = <main className="mode-shell"><section className="mode-card"><h1>Не удалось подключиться к серверу</h1><div className="mode-buttons"><button className="button button--primary" onClick={onRetry}>Повторить</button><button className="button button--secondary" onClick={onOffline}>Играть с ботами офлайн</button><button className="button button--secondary" onClick={onHome}>Меню</button></div></section></main>;
  else if (booting) content = <LoadingScreen />;
  else if (!state) {
    const joinMatch = window.location.pathname.match(/^\/join\/([A-Z0-9]{4})\/?$/i);
    content = <Lobby initialRoomCode={joinMatch?.[1]?.toUpperCase() ?? ''} connected={connected} createRoom={gameSocket.createRoom} joinRoom={gameSocket.joinRoom} />;
  }
  else if (state.game.status === 'waiting') content = <WaitingRoom room={state} action={gameSocket.action} connected={connected} leaveLocalSession={gameSocket.leaveLocalSession} onReturn={onHome} />;
  else content = <GameScreen room={state} action={gameSocket.action} connected={connected} leaveLocalSession={gameSocket.leaveLocalSession} onReturn={onHome} />;

  return (
    <>
      {content}
      {!state && !connectionFailed && <button className="menu-return button button--secondary" onClick={onHome}>Назад</button>}
      {error && (
        <aside className="error-toast" role="alert">
          <AlertTriangle size={20} />
          <span>{error}</span>
          <button type="button" onClick={clearError} aria-label="Закрыть"><X size={18} /></button>
        </aside>
      )}
      {!connected && state && <div className="offline-banner">Соединение потеряно. Игра сохранена на сервере, выполняется переподключение…</div>}
    </>
  );
}

function OfflinePlay({ controller, onHome }) {
  const [room, setRoom] = useState(controller.state);
  useEffect(() => {
    const unsubscribe = controller.subscribe(setRoom);
    controller.start();
    return () => { unsubscribe(); controller.close(); };
  }, [controller]);
  const leave = async () => { await controller.pause(); onHome(); };
  const player = room.game.players.find((item) => item.id === room.handoff?.playerId);
  const error = room.error && <aside className="error-toast" role="alert"><AlertTriangle size={20} /><span>{room.error}</span>{room.storageBlocked ? <button type="button" onClick={() => (room.saveConflict ? controller.reloadSave() : controller.retrySave()).catch(() => {})}>{room.saveConflict ? 'Загрузить актуальную партию' : 'Повторить сохранение'}</button> : <button onClick={() => controller.publish()} aria-label="Закрыть"><X size={18} /></button>}</aside>;
  if (room.handoff) return <main className="mode-shell"><section className="mode-card handoff-screen"><h1>{room.handoff.purpose === 'trade' ? 'Предложение сделки' : room.handoff.purpose === 'debt' ? 'Погашение долга' : 'Ход игрока'} {player?.name}</h1><p>Передайте устройство игроку {player?.name}</p><button className="button button--primary button--wide" disabled={room.storageBlocked} onClick={() => controller.confirmHandoff().catch(() => {})}>Начать ход</button><button className="button button--secondary" onClick={leave}>Меню</button>{error}</section></main>;
  return <><GameScreen key={room.viewerId} room={room} action={(event, payload) => controller.action(event, payload)} connected leaveLocalSession={leave} />{error}</>;
}

export default function App() {
  const [screen, setScreen] = useState(() => /^\/join\//i.test(window.location.pathname) || sessionStorage.getItem(ONLINE_VIEW_KEY) ? 'online' : 'menu');
  const [onlineResume, setOnlineResume] = useState(resumeOpenOnlineView);
  const [controller, setController] = useState(null);
  const [error, setError] = useState(null);
  const [onlineAttempt, setOnlineAttempt] = useState(0);
  const home = () => { sessionStorage.removeItem(ONLINE_VIEW_KEY); setScreen('menu'); setController(null); };
  const openOnline = (resumeSavedSession) => {
    sessionStorage.removeItem(ONLINE_VIEW_KEY);
    setOnlineResume(resumeSavedSession); setScreen('online');
  };
  const startOffline = async (config) => {
    const saved = await new OfflineSaveStore().read();
    if (saved?.game.status === 'active' && !window.confirm('Начать новую локальную партию вместо сохранённой?')) return;
    const next = await OfflineGameController.create(config, { expectedSave: saved });
    setController(next); setError(null); setScreen('offline');
  };
  const resume = async (mode) => {
    if (mode === 'online') { openOnline(true); return; }
    try { const next = await OfflineGameController.restore(); setController(next); setError(null); setScreen('offline'); }
    catch (failure) { setError(failure.message); }
  };
  if (screen === 'online') return <OnlinePlay key={onlineAttempt} resumeSavedSession={onlineResume} onHome={home} onOffline={() => setScreen('bots')} onRetry={() => setOnlineAttempt((value) => value + 1)} />;
  if (screen === 'offline') return <OfflinePlay controller={controller} onHome={home} />;
  if (screen === 'bots' || screen === 'hotseat') return <OfflineSetup key={screen} mode={screen} onStart={startOffline} onBack={home} />;
  return <MainMenu onOnline={() => openOnline(false)} onSetup={setScreen} onContinue={resume} error={error} />;
}
